import type {
  BookAppointmentPayload,
  BookAppointmentResponse,
  BookWindowPayload,
  BookWindowResponse,
  ChatMessageResponse,
  ChatSessionResponse,
  DoctorDto,
  PublicConfig,
  RequestOtpResponse,
  SlotDto,
  VerifyOtpResponse,
  WidgetOptions,
  WindowDto,
} from './types.js';

/**
 * Every request here carries only the public widget key. It never sees a
 * tenant ID, an admin secret, or a database credential — the server resolves
 * the tenant from the widget key itself (see apps/api plugins/auth.ts).
 */
export class WidgetApi {
  constructor(private opts: WidgetOptions) {}

  private async request<T>(path: string, body?: unknown, accessToken?: string): Promise<T> {
    const res = await fetch(`${this.opts.apiBaseUrl}${path}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Widget-Key': this.opts.widgetKey,
        ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
      },
      body: JSON.stringify(body ?? {}),
    });
    if (!res.ok) {
      let message = `Request failed (${res.status})`;
      try {
        const data = await res.json();
        message = data?.error?.message ?? message;
      } catch {
        // ignore
      }
      throw new Error(message);
    }
    return res.json() as Promise<T>;
  }

  private async get<T>(path: string): Promise<T> {
    const res = await fetch(`${this.opts.apiBaseUrl}${path}`, {
      headers: { 'X-Widget-Key': this.opts.widgetKey },
    });
    if (!res.ok) {
      let message = `Request failed (${res.status})`;
      try {
        const data = await res.json();
        message = data?.error?.message ?? message;
      } catch {
        // ignore
      }
      throw new Error(message);
    }
    return res.json() as Promise<T>;
  }

  async getPublicConfig(): Promise<PublicConfig> {
    const res = await fetch(`${this.opts.apiBaseUrl}/api/tenant/public-config`, {
      headers: { 'X-Widget-Key': this.opts.widgetKey },
    });
    if (!res.ok) throw new Error('Unable to load clinic configuration.');
    return res.json();
  }

  async startSession(): Promise<ChatSessionResponse> {
    return this.request('/api/chat/session', { widgetKey: this.opts.widgetKey });
  }

  async sendMessage(conversationId: string, message: string): Promise<ChatMessageResponse> {
    return this.request('/api/chat/message', { conversationId, message });
  }

  async getDoctors(): Promise<DoctorDto[]> {
    const { doctors } = await this.get<{ doctors: DoctorDto[] }>('/api/widget/doctors');
    return doctors;
  }

  async getAvailability(doctorId: string, fromIso: string, toIso: string): Promise<SlotDto[]> {
    const params = new URLSearchParams({ doctorId, from: fromIso, to: toIso });
    const { slots } = await this.get<{ slots: SlotDto[] }>(`/api/widget/availability?${params.toString()}`);
    return slots;
  }

  /** Date + working-window FCFS token booking (TOKEN_QUEUE doctors only — see DoctorDto.usesTokenQueue). */
  async getWindows(doctorId: string, date: string): Promise<WindowDto[]> {
    const params = new URLSearchParams({ doctorId, date });
    const { windows } = await this.get<{ windows: WindowDto[] }>(`/api/widget/windows?${params.toString()}`);
    return windows;
  }

  /** Step 1 of the required registration flow: send a one-time code to the patient's phone. */
  async requestOtp(phone: string): Promise<RequestOtpResponse> {
    return this.request('/api/widget/otp/request', { phone });
  }

  /** Step 2: verify the code the patient received. Returns a short-lived token that authorizes booking. */
  async verifyOtp(phone: string, code: string, fullName?: string): Promise<VerifyOtpResponse> {
    return this.request('/api/widget/otp/verify', { phone, code, ...(fullName ? { fullName } : {}) });
  }

  /** Email equivalent of requestOtp/verifyOtp above — used when a patient
   * books with only an email address (no WhatsApp/phone number at all). */
  async requestEmailOtp(email: string): Promise<RequestOtpResponse> {
    return this.request('/api/widget/otp/request-email', { email });
  }

  async verifyEmailOtp(email: string, code: string, fullName?: string): Promise<VerifyOtpResponse> {
    return this.request('/api/widget/otp/verify-email', { email, code, ...(fullName ? { fullName } : {}) });
  }

  /**
   * `patientAccessToken` comes from verifyOtp — required only while
   * PublicConfig.requiresPhoneVerificationForBooking is true (see widget.ts);
   * omit it when phone verification is switched off.
   */
  async bookAppointment(payload: BookAppointmentPayload, patientAccessToken?: string): Promise<BookAppointmentResponse> {
    return this.request('/api/widget/appointments', payload, patientAccessToken);
  }

  /** Same endpoint as bookAppointment — the API branches on the body's own
   * shape (windowStart/windowEnd vs startTime), not on tenant/doctor entitlement. */
  async bookWindow(payload: BookWindowPayload, patientAccessToken?: string): Promise<BookWindowResponse> {
    return this.request('/api/widget/appointments', payload, patientAccessToken);
  }
}
